import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import { Button } from '@/common/ui/button'
import BrokeFlexPresetSelector from '../../components/BrokeFlexPresetSelector'
import BrokeFlexPresetConfigForm from '../../components/BrokeFlexPresetConfigForm'
import BrokeFlexMessageCampaignForm from '../../components/BrokeFlexMessageCampaignForm'
import BrokeFlexMessageHelp from '../../components/BrokeFlexMessageHelp'
import useQueryGetBrokeFlexPresets from '../../api/hooks/useQueryGetBrokeFlexPresets'
import { Alert, AlertDescription } from '@/common/ui/alert'
import { Info } from 'lucide-react'

const CreateBrokeFlexMessagePage = () => {
	const navigate = useNavigate()
	const [selectedPreset, setSelectedPreset] = useState<string | null>(null)

	const { data: presetsData, isLoading } = useQueryGetBrokeFlexPresets()

	const handleSuccess = () => {
		navigate('/operator/messaging/broke-flex-message')
	}

	const selectedPresetData = presetsData?.data.find(
		(p) => p.preset === selectedPreset,
	)

	const showAdvancedForm = selectedPreset === 'CUSTOM'

	return (
		<PageContainer>
			<div className="space-y-6">
				<div className="flex items-start justify-between">
					<div>
						<h1 className="text-3xl font-bold">
							สร้างแคมเปญข้อความเมื่อผู้เล่นเงินหมด
						</h1>
						<p className="mt-2 text-muted-foreground">
							ส่งข้อความพร้อมโบนัสให้ผู้เล่นที่เงินหมดและยังไม่ฝากเงินเพิ่ม
							เพื่อดึงดูดให้กลับมาเล่นอีกครั้ง
						</p>
					</div>
					<BrokeFlexMessageHelp />
				</div>

				<Alert>
					<Info className="h-4 w-4" />
					<AlertDescription className="text-base">
						เริ่มต้นด้วยการเลือกรูปแบบแคมเปญที่เหมาะกับคุณ
						คุณสามารถเพิ่มระยะการส่งข้อความเพิ่มเติม (ระยะที่ 2, 3, 4)
						ได้ภายหลังจากสร้างแคมเปญแล้ว
					</AlertDescription>
				</Alert>

				{!selectedPresetData && !showAdvancedForm ? (
					<BrokeFlexPresetSelector
						presets={presetsData?.data || []}
						selectedPreset={selectedPreset}
						onSelectPreset={setSelectedPreset}
						isLoading={isLoading}
					/>
				) : showAdvancedForm ? (
					<div className="space-y-6">
						<div className="flex items-center justify-between">
							<div>
								<h2 className="text-xl font-semibold">
									สร้างแคมเปญแบบกำหนดเอง
								</h2>
								<p className="mt-1 text-sm text-muted-foreground">
									กำหนดค่าทุกอย่างด้วยตัวเอง รวมถึงระยะและรายการ
								</p>
							</div>
							<Button variant="ghost" onClick={() => setSelectedPreset(null)}>
								กลับไปเลือกรูปแบบ
							</Button>
						</div>
						<BrokeFlexMessageCampaignForm
							mode="create"
							onSuccess={handleSuccess}
						/>
					</div>
				) : (
					<BrokeFlexPresetConfigForm
						preset={selectedPresetData!}
						onSuccess={handleSuccess}
						onCancel={() => setSelectedPreset(null)}
					/>
				)}
			</div>
		</PageContainer>
	)
}

const GuardedCreateBrokeFlexMessagePage = withUserPermission(
	CreateBrokeFlexMessagePage,
	{
		permissionType: PermissionType.Write,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedCreateBrokeFlexMessagePage as CreateBrokeFlexMessagePage }
